import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useRef } from 'react';
import { Resolver, useForm, useWatch } from 'react-hook-form';

import { buildStrategyDraft } from '../backtesting-provider.helpers';
import { StrategyFormData } from '../types';
import { AllToggles, createStrategySchema, DEFAULT_TOGGLES } from '../validation/schemas/strategy';

export const useStrategyForm = (defaults?: Partial<StrategyFormData>) => {
  const defaultValues = buildStrategyDraft(defaults);

  const schemaRef = useRef(createStrategySchema(DEFAULT_TOGGLES));

  const form = useForm<StrategyFormData>({
    resolver: (values, context, options) =>
      (zodResolver(schemaRef.current) as Resolver<StrategyFormData>)(values, context, options),
    defaultValues,
    mode: 'onSubmit',
  });

  const toggleFields = useWatch({
    control: form.control,
    name: [
      'execution.includePerpetualSpread',
      'execution.includeVolSpread',
      'execution.includePerpetualFee',
      'execution.includeOptionsFee',
      'execution.whenExecutionWeekdays',
      'execution.whenExecutionAverageIn',
      'execution.optionsNotionalScaled',
      'hedging.deltaThreshold',
      'hedging.deltaThresholdUpper',
      'hedging.deltaThresholdLower',
      'hedging.deltaTarget',
    ],
  });

  const currentToggles: AllToggles = useMemo(
    () => ({
      includePerpetualSpread: toggleFields[0] ?? false,
      includeVolSpread: toggleFields[1] ?? false,
      includePerpetualFee: toggleFields[2] ?? false,
      includeOptionsFee: toggleFields[3] ?? false,
      whenExecutionWeekdays: toggleFields[4] ?? false,
      whenExecutionAverageIn: toggleFields[5] ?? false,
      optionsNotionalScaled: toggleFields[6] ?? false,
      deltaThreshold: toggleFields[7] ?? false,
      deltaThresholdUpper: toggleFields[8] ?? false,
      deltaThresholdLower: toggleFields[9] ?? false,
      deltaTarget: toggleFields[10] ?? false,
    }),
    [toggleFields],
  );

  useEffect(() => {
    schemaRef.current = createStrategySchema(currentToggles);
  }, [currentToggles]);

  return form;
};
